import {Category, ResolvedContactMethod} from '@wix/answers-api';
import { createMemoryHistory } from 'react-router';
import * as scrollToView from 'scroll-into-view';
export const scrollIntoView = scrollToView;

export declare type WidgetResolvedTicketSettings = {
	title: string;
	subTitle: string;
	contactMethod: ResolvedContactMethod;
	responseTimeMessage: string;
};

export const answersApiUrl = (tenant: string, useMock = false) => {
	return useMock ? `/dev-proxy/${tenant}/api/v1` : `https://${tenant}.wixanswers.com/api/v1`;
};

export const delay = <T>(cb: () => T, time: number): Promise<T> => new Promise((resolve) => {
	setTimeout(() => resolve(cb()), time);
});

export const waitUntilTruthy = async (expr: () => boolean | string, max = 2000, intervalTime = 50) => {
	let time = 0;

	const checkChunk = delay(() => expr(), intervalTime);

	let lastResult;

	while (time < max) {
		lastResult = await checkChunk;
		if (lastResult === true) {
			return true;
		}
		time += intervalTime;
	}

	const defaultExplanation = 'waiting for expression to become thruthy';
	const explanation = typeof lastResult === 'string' ? lastResult : defaultExplanation;
	const msg = `Timeout while ${explanation}`;
	throw new Error(msg);
};

export const createAnswersMemoryHistory = (basePath: string, currentUrl: string) => {
	const pathnameForHistory = currentUrl.replace(basePath, '');
	return (createMemoryHistory as any)({entries: [pathnameForHistory], basename: basePath});
};

export const pickTitle = (obj: {title: string}) => obj.title;
export const pickName = (obj: {name: string}) => obj.name;

export const logger = (...args: any[]) => {
	const toStr = (arg: any) => typeof arg === 'object' ? JSON.stringify(arg) : arg;
	const resilientToStr = (str: any) => {
		try {
			return toStr(str);
		} catch (e) {
			return str;
		}
	};
	const strArgs = args.map(resilientToStr).join(', ');
	const msg = `[${Date.now()}] - ${strArgs}`;
	console.info(msg);
};

export const objectKeys = <T extends {}>(a: T): Array<(keyof T)> => {
	const keys = Object.keys(a) as Array<(keyof T)>;
	return keys;
};

export interface CompleteCategory extends Category {
	children: CompleteCategory[];
}

export const normalizeCategory = (category: Category): CompleteCategory => {
	const children = (category.children || []).map(normalizeCategory);
	// tslint:disable-next-line:prefer-object-spread
	return Object.assign({}, {children}, category);
};

export type CategoryMap = {
	[key: string]: CompleteCategory
};

// tslint:disable-next-line:no-var-requires
export const Upload = require('rc-upload');

export const categoriesToCategoryMap = (categories: CompleteCategory[]): CategoryMap => {
	return categories.reduce((categoryMap: CategoryMap, category: CompleteCategory) => {
		if (category.children.length) {
			const childrenCategoryMap = categoriesToCategoryMap(category.children.map(normalizeCategory));
			categoryMap = {...childrenCategoryMap, ...categoryMap}; // Object.assign({}, childrenCategoryMap, categoryMap);
		}
		categoryMap[category.id] = category;
		return categoryMap;
		// return {...categoryMap, [category.id]: category };
	}, {});
};

export interface ClassDictionary {
	[id: string]: boolean;
}
// tslint:disable-next-line:no-empty-interface
export interface ClassArray extends Array<ClassValue> { }
export type ClassValue = string | number | ClassDictionary | ClassArray | any;
// tslint:disable-next-line:no-var-requires
const realClassNames = require('classnames');
export const classNames = (...classes: ClassValue[]) => {
	return realClassNames(...classes);
};

export const updateFontStyleTag = (url: string, name: string) => {
	if (window) {
		const widgetHead = window.document.head;
		const linkRelId = 'widget-preview-dynamic-font';
		const styleTagId = 'widget-preview-dynamic-font-apply';

		const maybeExistingLinkTag = widgetHead.querySelector(`#${linkRelId}`);
		const maybeExistingStyleTag = widgetHead.querySelector(`#${styleTagId}`);

		const linkTag: any = maybeExistingLinkTag || document.createElement('link');
		const styleTag: any = maybeExistingStyleTag || document.createElement('style');

		linkTag.rel = 'stylesheet';
		linkTag.id = linkRelId;
		linkTag.href = url;

		styleTag.id = styleTagId;
		styleTag.innerHTML = `body { font-family: '${name}'}`;

		if (!maybeExistingLinkTag) {
			widgetHead.appendChild(linkTag);
		}

		if (!maybeExistingStyleTag) {
			widgetHead.appendChild(styleTag);
		}
	}
};

export const closeWidget = () => {
	parent.postMessage({closeWidget: true}, '*');
};

export const contentToHtml = (content: string): string => {
	return content.split('\n').map((sentence) => `<p>${sentence}</p>`).join('\n');
};
